import { useFormik } from 'formik';
import {
  AutopopulateAccommodationPlacesScenario,
  CreateEventMutation,
  CreateEventPayload0301,
  CreateEventPlaceMutation,
  EventPlaceType,
  EventType,
  EventUserStatus,
  ScoringPriorityField,
  useCreateEventPlaceMutation,
  useTrackImpactRadiusMutation,
  useUpdateTravelSettingsMutation,
} from '@graphql/generated';
import { useAuth } from '@shared/components/AuthProvider';
import * as Yup from 'yup';
import { getLocalStorage, useIdentify, useSegmentGroupCallForEvent, useTranslation } from '@shared/core';
import { useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { mapCurrencyCodeToTravelCurrencyCode } from '@shared/utils/currency/currencyMapper';
import { useEffect, useMemo, useState } from 'react';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { addMonths } from 'date-fns';
import { useDesignParams } from '@apps/createWedding/hooks/useDesignParams';
import { getTelemetryDateLabel, omitEmpty } from '@apps/createWedding/utils';
import { useFieldErrors } from '@apps/createWedding/hooks/useFieldErrors';
import { useCreateWeddingTelemetry } from '@apps/createWedding/CreateWedding.telemetry';
import { createAndAttachPixelImage } from '@apps/createWedding/components/PixelImage/PixelImage';
import { getTimezone } from '@apps/createWedding/utils/getTimezone';
import { CREATE_WEDDING_APP_NAME } from '@apps/createWedding/constants';
import { useViewContext } from '@apps/createWedding/components/View/ViewProvider';
import { useEventState } from '@apps/createWedding/EventState';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { readAcquisitionContexts, readAndResetAcquisitionContexts } from '@shared/createEvent/acquisitionContextTracking';
import { StoredAcquisitionContextDetails } from '@shared/createEvent/acquisitionContextTracking/acquisitionContextTracking.types';
import { addAction, addError } from '@shared/utils/logger';
import { getEventTypeConfig } from '@config';
import { getCreateEventPayload } from '@shared/utils/impactRadius/utilsImpactRadius';
import { useDefaultTemplatePayload, useWebsiteSettingsFromQueryParam } from './hooks';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useDisclosure } from '@withjoy/joykit';
import { FetchResult } from '@apollo/client';
import { useAutoPopulateAccommodationPlacesV4 } from '@shared/utils/accommodations/useAutoPopulateAccommodationPlacesV4';
import { setAutoPopulatedAccommodationsCount, useAutoPopulateAccommodationPlacesV5 } from '@shared/utils/accommodations';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';

const CREATE_WEDDING_KEY = '@@createwedding';

export const PENDING_CREATION_KEY = '@@createwedding/pending-creation';

export interface EventVenueLocation
  extends Readonly<{
    placeId?: string;
    locationValue?: string;
    displayName?: string;
    address?: string;
    city?: string;
    latitude?: number;
    longitude?: number;
  }> {}

export type CreateWeddingFormFields = {
  ownerFirstName: string;
  partnerFirstName: string | null;
  ownerLastName: string | null;
  weddingDate: Date | null;
  isDateValid: boolean;
  location: Location | null;
  eventVenue: EventVenueLocation | null;
  font?: string;
  template?: string;
  scenario?: string;
  theme?: string;
};

const AUTO_POPULATE_DEFAULT_RADIUS_KM = 5;

export type OnEventCreateSuccessHandler = (data: NonNullable<CreateEventMutation['createEventV0301']>) => void;

export interface CreateWeddingFormProps {
  disableQuestionnaire?: boolean;
  skipWaitForDesignParamToSettle?: boolean;
  onAfterEventCreateInitiated?: () => void;
  onEventCreateSuccess?: OnEventCreateSuccessHandler;
  isCreateCtaLoading?: boolean;
  logoutRedirectUri?: string;
}

interface UseCreateWeddingFormControllerArgs extends CreateWeddingFormProps {}

export const useCreateWeddingFormController = (args: UseCreateWeddingFormControllerArgs) => {
  const { disableQuestionnaire, logoutRedirectUri, onAfterEventCreateInitiated, onEventCreateSuccess, skipWaitForDesignParamToSettle } = args;
  const authProvider = useAuth();
  const { identify } = useIdentify();
  const viewContext = useViewContext(false);
  const { createEvent, addEventCreatedListener } = useEventState();
  const localStorage = getLocalStorage();
  const { t, tq } = useTranslation('createWedding');
  const { designParams, settled } = useDesignParams();
  const telemetry = useCreateWeddingTelemetry();
  const [blockWeddingDate, setBlockWeddingDate] = useState<boolean>(false);
  const [blockEventVenue, setBlockEventVenue] = useState<boolean>(false);
  const [trackImpactRadius] = useTrackImpactRadiusMutation();
  const enableBlankDates = useFeatureValue('enableBlankDates');
  const { getWebsiteDesignSettings, getWebsiteHeaderLayout } = useWebsiteSettingsFromQueryParam();
  const applyDefaultTemplatePayload = useDefaultTemplatePayload();
  const queryParamHelper = useQueryParamHelper();
  const customEventQueryParam = queryParamHelper.getValueString('customEvent');
  const { isOpen: isCustomEventDialogOpen, onClose: onCloseCustomEventDialog, onOpen: onOpenCustomEventDialog } = useDisclosure();
  const [isCreateWeddingHeaderLayoutExperimentEnabled] = useIsFeatureEnabled('createWeddingHeaderLayoutExperiment');

  const { executeSegmentGroupCallForEvent } = useSegmentGroupCallForEvent();
  const [createEventPlaceMutation] = useCreateEventPlaceMutation();
  const [updateTravelSettings] = useUpdateTravelSettingsMutation();
  const { currency } = useGeoBasedLocale();

  const [createWeddingUpdateV1ExperimentEnabled] = useIsFeatureEnabled('createWeddingUpdateV1Experiment');
  const [asyncHotelAutopopulationEnabled] = useIsFeatureEnabled('asyncHotelAutopopulationEnabled');

  const { callAutoPopulateAccommodationPlacesV4 } = useAutoPopulateAccommodationPlacesV4();
  const { callAutoPopulateAccommodationPlacesV5 } = useAutoPopulateAccommodationPlacesV5();

  const autoPopulateAccommodationsForEvent = useEventCallback(async (eventId: string, location: { latitude: number; longitude: number }, venueGooglePlaceId?: string) => {
    const commonPayload = {
      eventId,
      location,
      scoringPriorityFields: [ScoringPriorityField.standardRoomPriceDiscountPercent, ScoringPriorityField.userRating, ScoringPriorityField.distanceFromSearchLocation],
      context: 'CreateWedding',
      searchRadiusKm: AUTO_POPULATE_DEFAULT_RADIUS_KM,
      venueGooglePlaceId,
    };
    try {
      if (asyncHotelAutopopulationEnabled) {
        await callAutoPopulateAccommodationPlacesV5({ ...commonPayload, scenario: AutopopulateAccommodationPlacesScenario.eventCreate });
      } else {
        const autoPopulatedAccommodations = await callAutoPopulateAccommodationPlacesV4(commonPayload);
        setAutoPopulatedAccommodationsCount(eventId, { newlyPopulatedCount: autoPopulatedAccommodations.length });
      }
    } catch (error) {
      addError(error, {
        context: 'CreateWedding/autoPopulateAccommodationsForEvent',
        eventId,
        isAsync: asyncHotelAutopopulationEnabled,
      });
    }
  });

  const waitForDesignDeferred = useMemo(() => {
    let outsideResolve: (value?: unknown) => void = () => {};
    const promise = new Promise(resolve => {
      outsideResolve = resolve;
    });
    return { promise, resolve: outsideResolve };
  }, []);

  const shouldResolveDesignPromise = settled || skipWaitForDesignParamToSettle;
  const headerLayout = useMemo(
    () => (isCreateWeddingHeaderLayoutExperimentEnabled ? getWebsiteHeaderLayout() : null),
    [getWebsiteHeaderLayout, isCreateWeddingHeaderLayoutExperimentEnabled],
  );
  useEffect(() => {
    if (shouldResolveDesignPromise) {
      waitForDesignDeferred.resolve();
    }
  }, [shouldResolveDesignPromise, waitForDesignDeferred]);

  const errorTranslations = t('weddingForm', 'errors');

  const initialValuesFromLocalStorage = useMemo(() => {
    const values = localStorage.getItem(CREATE_WEDDING_KEY);

    if (!values) {
      return;
    }

    const parsedVal = JSON.parse(values);

    return { ...parsedVal, ...(parsedVal.weddingDate ? { weddingDate: new Date(parsedVal.weddingDate) } : {}) };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTrackImpactRadius = async (event: CreateEventMutation) => {
    try {
      const eventTypeConfig = getEventTypeConfig(EventType.wedding);
      const payload = getCreateEventPayload(
        eventTypeConfig.impactRadiusCampaignId,
        eventTypeConfig.impactRadiusCreateEventActionTrackerId,
        event.createEventV0301?.id,
        authProvider.currentUser.profile?.activeAlias?.auth0Id,
      );

      if (!payload) {
        return;
      }

      await trackImpactRadius({ variables: { payload } });
    } catch (e) {
      telemetry.trackError('CreateWeddingTrackImpactRadius', e);
    }
  };

  const handleCreateEvent = async (values: CreateWeddingFormFields) => {
    const { timezoneId, timezoneMethod } = getTimezone();
    const eventDate = blockWeddingDate ? null : values.weddingDate;
    const eventDateToIsoString = eventDate?.toISOString() || null;
    const durationDays = eventDate ? Math.ceil((eventDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : undefined;

    telemetry.dateSet(eventDateToIsoString ?? 'dateUndecided', getTelemetryDateLabel(eventDate), durationDays);
    telemetry.timezoneRegistered({ timezoneId, timezoneMethod });
    telemetry.submitDetailsClicked(authProvider.currentUser?.profile?.email ?? '');
    const acquisitionContext = readAcquisitionContexts();
    let forceHotelBlockEnabled = false;
    const checkAcquisitionContextData = (dataDetails: StoredAcquisitionContextDetails, key: string, value: string) => {
      for (const item of dataDetails.data) {
        if (item.key === key && item.value === value) {
          return true;
        }
      }
      return false;
    };

    acquisitionContext.forEach(item => {
      if (item?.relativeUrl?.includes('hotel-room-blocks') || checkAcquisitionContextData(item, 'utm_campaign', '[SAGE]-Wedding-Website-New-MarComm-Travel-Segments-Only')) {
        forceHotelBlockEnabled = true;
      }
    });

    addAction('checkForceHotelBlockEnabled', { acquisitionContext, forceHotelBlockEnabled, userId: authProvider.currentUser?.profile?.id });

    await waitForDesignDeferred.promise;

    const eventLocation = blockEventVenue ? {} : { lat: values.location?.lat, lng: values.location?.lng, placeId: values.location?.placeId, placeName: values.location?.place };
    let payload: CreateEventPayload0301 = omitEmpty({
      ownerFirstName: values.ownerFirstName,
      fianceeFirstName: values.partnerFirstName,
      ownerLastName: values.ownerLastName,
      fianceeLastName: '',
      font: values.font,
      template: values.template,
      mainTzid: timezoneId,
      theme: values.theme,
      mainDate: enableBlankDates ? eventDateToIsoString : eventDateToIsoString || addMonths(new Date(), 9).toISOString(),
      acquisitionContext: {
        currentTimestamp: Date.now(),
        details: readAndResetAcquisitionContexts(),
      },
      ...eventLocation,
    });

    const websiteDesignSettings = getWebsiteDesignSettings();

    if (websiteDesignSettings) {
      payload.websiteDesignSettings = websiteDesignSettings;
    }

    if (isCreateWeddingHeaderLayoutExperimentEnabled && headerLayout) {
      payload.headerLayout = headerLayout;
    }

    if (!websiteDesignSettings && !headerLayout) {
      const { payload: defaultTemplatePayload, error } = await applyDefaultTemplatePayload(payload);
      if (error) {
        formik.setSubmitting(false);
        return;
      }
      payload = defaultTemplatePayload;
    }

    createEvent({
      variables: {
        payload,
      },
    });
    localStorage.setItem(CREATE_WEDDING_KEY, undefined);
    createAndAttachPixelImage({ action: 'lead' });
    addEventCreatedListener(async event => {
      if (event.createEventV0301) {
        const { id: eventId, firebaseId, website, info } = event.createEventV0301!;

        executeSegmentGroupCallForEvent(firebaseId, {
          postgresEventId: eventId,
          eventType: info.eventType,
        });
        const adminOrOwnerEventCount = authProvider.currentUser?.eventMemberships?.filter(x => x.status === EventUserStatus.admin || x.status === EventUserStatus.owner).length;
        telemetry.weddingCreated({
          website,
          eventId: firebaseId,
          email: authProvider.currentUser.profile?.email ?? '',
          adminOrOwnerEventCount,
          venueLocationProvided: !blockEventVenue && !!values.eventVenue?.placeId,
          venueLocationSkipped: blockEventVenue,
          eventLocationProvided: !!eventLocation.placeName,
          customEventLocationUsed: !!eventLocation.placeName && !eventLocation.placeId,
          lastNameProvided: !!values.ownerLastName,
        });
        handleTrackImpactRadius(event);

        try {
          await updateTravelSettings({
            variables: {
              payload: {
                eventId,
                currencyCode: mapCurrencyCodeToTravelCurrencyCode(currency),
              },
            },
          });
        } catch (error) {
          addError(error, { context: 'CreateWedding/updateTravelSettings', eventId, currency });
        }

        if (createWeddingUpdateV1ExperimentEnabled && !blockEventVenue && values.eventVenue?.placeId) {
          await createEventPlaceMutation({ variables: { payload: { googlePlaceId: values.eventVenue.placeId, eventId, type: EventPlaceType.venue } } })
            .then(async (result: FetchResult<CreateEventPlaceMutation> | void) => {
              if (result) {
                const venue = result.data?.createEventPlace;
                if (venue && venue.googlePlaceId && venue.latitude && venue.longitude) {
                  await autoPopulateAccommodationsForEvent(eventId, { latitude: venue.latitude, longitude: venue.longitude }, venue.googlePlaceId);
                } else {
                  addError('Missing Venue Info', {
                    context: 'Event Place',
                    message: `Failed to createEventPlace - eventId: ${eventId}, googlePlaceId: ${values.eventVenue?.placeId}`,
                  });
                }
              } else {
                addError('Missing Results', { context: 'Event Place', message: `Failed to createEventPlace - eventId: ${eventId}, googlePlaceId: ${values.eventVenue?.placeId}` });
              }
            })
            .catch(error => {
              addError(error, { context: 'Event Place', message: `Failed to createEventPlace - eventId: ${eventId}, googlePlaceId: ${values.eventVenue?.placeId}` });
            });
        } else if (!createWeddingUpdateV1ExperimentEnabled && values.location?.lat && values.location?.lng) {
          await autoPopulateAccommodationsForEvent(eventId, { latitude: values.location?.lat, longitude: values.location?.lng });
        }

        identify({ forceHotelBlockEnabled });

        onEventCreateSuccess?.(event.createEventV0301);
      }
    });

    if (!disableQuestionnaire) {
      viewContext?.changeView('questionnaire');
    }

    onAfterEventCreateInitiated?.();
  };

  const formik = useFormik<CreateWeddingFormFields>({
    initialValues: {
      ownerFirstName: '',
      ownerLastName: '',
      partnerFirstName: '',
      weddingDate: addMonths(new Date(), 9),
      location: null,
      eventVenue: null,
      ...initialValuesFromLocalStorage,
      ...designParams,
      isDateValid: true,
    },
    validationSchema: Yup.object<CreateWeddingFormFields>({
      ownerFirstName: Yup.string().required(errorTranslations.firstNameRequired()),
      ownerLastName: Yup.string().nullable(),
      partnerFirstName: Yup.string().nullable(),
      weddingDate: Yup.date()
        .nullable()
        .test('valid date', errorTranslations.invalidDate(), function () {
          const isDateValid = this.parent.isDateValid;
          return blockWeddingDate || isDateValid;
        }),
      isDateValid: Yup.boolean(),
      location: (
        Yup.object<Location>({
          place: Yup.string().when([], {
            is: () => createWeddingUpdateV1ExperimentEnabled,
            then: Yup.string().notRequired(),
            otherwise: Yup.string().required(),
          }),
          lng: Yup.number(),
          lat: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<Location>
      )
        .nullable(true)
        .when([], {
          is: () => !createWeddingUpdateV1ExperimentEnabled,
          then: (schema: Yup.ObjectSchema<Location>) => schema.required(errorTranslations.locationRequired()),
          otherwise: (schema: Yup.ObjectSchema<Location>) => schema.notRequired(),
        }),

      eventVenue: (
        Yup.object<EventVenueLocation>({
          locationValue: Yup.string().when([], {
            is: () => blockEventVenue,
            then: Yup.string().notRequired(),
            otherwise: Yup.string().required(tq`Add your venue, even if it’s not final, to get tailored recommendations for your big day.`),
          }),
          latitude: Yup.number(),
          longitude: Yup.number(),
          placeId: Yup.string(),
        }) as Yup.ObjectSchema<EventVenueLocation>
      )
        .nullable(true)
        .when([], {
          is: () => createWeddingUpdateV1ExperimentEnabled && !blockEventVenue,
          then: (schema: Yup.ObjectSchema<EventVenueLocation>) => schema.required(tq`Add your venue, even if it’s not final, to get tailored recommendations for your big day.`),
          otherwise: (schema: Yup.ObjectSchema<EventVenueLocation>) => schema.notRequired(),
        }),
      font: Yup.string(),
      template: Yup.string(),
      scenario: Yup.string(),
      theme: Yup.string(),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values, formikHelpers) => {
      formikHelpers.setFieldTouched('eventVenue', true, false);
      handleCreateEvent(values);
    },
  });

  useEffect(() => {
    if (customEventQueryParam?.length) {
      onOpenCustomEventDialog();
      formik.setFieldValue('ownerFirstName', customEventQueryParam || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customEventQueryParam, onOpenCustomEventDialog]);

  const getFieldErrorProps = useFieldErrors<CreateWeddingFormFields>(formik);

  useEffect(() => {
    localStorage.setItem(CREATE_WEDDING_KEY, JSON.stringify(formik.values));
  }, [formik.values]); // eslint-disable-line react-hooks/exhaustive-deps

  const signOut = () => authProvider.provideLogout({}, logoutRedirectUri ?? `${window.location.origin}/${CREATE_WEDDING_APP_NAME}`);

  return {
    formik,
    user: authProvider.currentUser.profile!,
    getFieldErrorProps,
    signOut,
    blockWeddingDate,
    setBlockWeddingDate,
    blockEventVenue,
    setBlockEventVenue,
    telemetry,
    isCustomEventDialogOpen,
    onCloseCustomEventDialog,
    createWeddingUpdateV1ExperimentEnabled,
  };
};
